import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock, RotateCcw } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { AreaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, hojeIso, mesAno, nomePessoa, textoLivre } from '../../nucleo/formato';
import { precisaEscolherEmpresa } from '../../nucleo/empresa/empresa-do-registro';
import { useSessao } from '../../nucleo/sessao';
import { horas, invalidarPonto, ultimosMeses, type Fechamento, type ItemFechamento } from './tipos';

/** Fechamento do ponto por empresa e mês: trava marcações e ajustes, lança o banco de horas e entrega o resultado à Folha. */
export function Fechamentos() {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  const [aberto, setAberto] = useState<Fechamento | 'novo' | null>(null);
  const colunas: Coluna<Fechamento>[] = [
    { chave: 'ano_mes', rotulo: t('campos.mes'), principal: true, render: (f) => <span className="font-medium">{mesAno(f.ano_mes)}</span> },
    { chave: 'empresa_nome', rotulo: t('campos.empresa'), render: (f) => f.empresa_nome ?? '-' },
    { chave: 'estado', rotulo: t('campos.estado'), render: (f) => <Selo cor={f.estado === 'fechado' ? 'verde' : 'neutro'}>{t(`fechamento.${f.estado}`)}</Selo> },
    { chave: 'funcionarios', rotulo: t('campos.funcionarios'), alinhar: 'direita', mono: true },
    { chave: 'folha_he50_min', rotulo: t('campos.folhaHe50'), alinhar: 'direita', mono: true, render: (f) => horas(f.folha_he50_min) },
    { chave: 'folha_desconto_min', rotulo: t('campos.folhaDesconto'), alinhar: 'direita', mono: true, render: (f) => horas(f.folha_desconto_min) },
    { chave: 'fechado_em', rotulo: t('campos.fechadoEm'), oculta: true, render: (f) => dataHora(f.fechado_em) },
  ];
  return (
    <>
      {s.pode('ponto.fechar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Lock className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.fecharMes')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Fechamento> tela="ponto.fechamentos" caminho="/ponto/fechamentos" chaveConsulta={['ponto', 'fechamentos']} colunas={colunas} aoClicar={(f) => setAberto(f)} vazio={<Vazio texto={t('vazio.fechamentos')} />} />
      {aberto === 'novo' && <FecharMes aoFechar={() => setAberto(null)} />}
      {aberto && aberto !== 'novo' && <FichaFechamento fechamento={aberto} aoFechar={() => setAberto(null)} />}
    </>
  );
}

function FecharMes({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const meses = ultimosMeses(hojeIso(), 12, false);
  const [mes, setMes] = useState(meses[0]!);
  // V2 verticais: sem empresa na aba (sem filtro, várias permitidas), a empresa do fechamento é escolhida aqui.
  const escolherEmpresa = precisaEscolherEmpresa(s.empresas);
  const [escolhida, setEscolhida] = useState('');
  const empresa = s.empresas?.atual ?? (escolhida || null);
  const nomeEmpresa = s.empresas?.opcoes.find((e) => e.id === empresa)?.nome;
  const fechar = useMutation({
    mutationFn: () => post('/ponto/fechamentos', { id: crypto.randomUUID(), ano_mes: mes, ...(empresa ? { empresa_id: empresa } : {}) }),
    onSuccess: async () => {
      await invalidarPonto(qc);
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={nomeEmpresa ? `${t('acoes.fecharMes')} · ${nomeEmpresa}` : t('acoes.fecharMes')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" carregando={fechar.isPending} aria-disabled={!empresa} onClick={() => empresa && fechar.mutate()}>
            {t('acoes.fecharMes')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 text-sm text-tinta-3">{empresa ? t('aviso.fecharEmpresa') : t('aviso.escolhaEmpresa')}</p>
      {escolherEmpresa && (
        <div className="mb-4">
          <Rotulo id="ponto-fechar-empresa" rotulo={tn('empresa.rotulo')} obrigatorio>
            <Selecao id="ponto-fechar-empresa" vazio="" value={escolhida} onChange={(e) => setEscolhida(e.target.value)} opcoes={(s.empresas?.opcoes ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }))} />
          </Rotulo>
        </div>
      )}
      <Rotulo id="ponto-fechar-mes" rotulo={t('campos.mes')}>
        <Selecao id="ponto-fechar-mes" value={mes} onChange={(e) => setMes(e.target.value)} opcoes={meses.map((m) => ({ valor: m, rotulo: mesAno(m) }))} />
      </Rotulo>
    </Modal>
  );
}

function FichaFechamento({ fechamento: f, aoFechar }: { fechamento: Fechamento; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [reabrindo, setReabrindo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const q = useQuery({ queryKey: ['ponto', 'fechamentos', f.id], queryFn: () => get<Fechamento & { itens: ItemFechamento[] }>(`/ponto/fechamentos/${f.id}`) });
  const reabrir = useMutation({
    mutationFn: () => post(`/ponto/fechamentos/${f.id}/reabrir`, { motivo }),
    onSuccess: async () => {
      await invalidarPonto(qc);
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <>
      <Drawer
        aberto={!reabrindo}
        aoFechar={aoFechar}
        largura="lg"
        titulo={`${mesAno(f.ano_mes)} · ${f.empresa_nome ?? ''}`}
        rodape={
          f.estado === 'fechado' && s.pode('ponto.fechar') ? (
            <Botao icone={<RotateCcw className="size-4" />} onClick={() => setReabrindo(true)}>
              {t('acoes.reabrir')}
            </Botao>
          ) : undefined
        }
      >
        <dl className="mb-4 text-sm">
          <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
            <dt className="text-tinta-3">{t('campos.fechadoPor')}</dt>
            <dd className="text-right text-tinta-1">{`${nomePessoa(f.fechado_por_nome) || '-'} · ${dataHora(f.fechado_em)}`}</dd>
          </div>
          {f.reaberto_em && (
            <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
              <dt className="text-tinta-3">{t('campos.reabertoPor')}</dt>
              <dd className="min-w-0 text-right text-tinta-1">{`${nomePessoa(f.reaberto_por_nome) || '-'} · ${dataHora(f.reaberto_em)} · ${textoLivre(f.motivo_reabertura)}`}</dd>
            </div>
          )}
        </dl>
        <Estado consulta={q}>
          {(d) => (
            <ul className="flex flex-col gap-3">
              {d.itens.map((i) => (
                <li key={i.funcionario_id} className="rounded-lg border border-borda p-3 text-sm">
                  <p className="mb-2 font-medium text-tinta-1">{nomePessoa(i.funcionario_nome)}</p>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {(
                      [
                        ['trabalhado', horas(i.trabalhado_min)],
                        ['esperado', horas(i.esperado_min)],
                        ['folhaHe50', horas(i.folha_he50_min)],
                        ['folhaHe100', horas(i.folha_he100_min)],
                        ['folhaDesconto', horas(i.folha_desconto_min)],
                        ['faltasDias', String(i.faltas_dias)],
                        ['regime', t(`regime.${i.regime}`)],
                        ['saldo', horas(i.banco_saldo_min, true)],
                      ] as const
                    ).map(([k, v]) => (
                      <div key={k} className="flex min-w-0 flex-wrap justify-between gap-1">
                        <dt className="text-tinta-3">{t(`campos.${k}`)}</dt>
                        <dd className="numero text-tinta-1">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </li>
              ))}
            </ul>
          )}
        </Estado>
      </Drawer>
      <Modal
        aberto={reabrindo}
        aoFechar={() => setReabrindo(false)}
        titulo={t('acoes.reabrir')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setReabrindo(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={reabrir.isPending} aria-disabled={!motivo.trim()} onClick={() => motivo.trim() && reabrir.mutate()}>
              {t('acoes.reabrir')}
            </Botao>
          </>
        }
      >
        <p className="mb-3 text-sm text-tinta-3">{t('aviso.reabrir')}</p>
        <Rotulo id="ponto-reabrir-motivo" rotulo={t('campos.motivo')} obrigatorio>
          <AreaSensivel
            id="ponto-reabrir-motivo"
            rows={3}
            maxLength={500}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </Modal>
    </>
  );
}
